import { memo } from 'react';

import { ImageIcon } from '@air/design-icons';

import { WorkspaceAgentMentionImageRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionImageRow';
import { type WorkspaceAgentBrandKitMentionItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';

/**
 * ImageRow draws this when `thumbnailUrl` is falsy. Kit rows always have a still (the mapper drops
 * values with none), so this fills the shared slot rather than standing in for a load error — there
 * is no `onError` on the thumbnail.
 */
const BRAND_KIT_FILE_GLYPH = <ImageIcon aria-hidden size="xs" />;

export type WorkspaceAgentBrandKitMentionRowProps = {
  /** DOM id so the composer can point `aria-activedescendant` at the highlighted row. */
  id: string;
  index: number;
  /** This row holds the highlight *and* the keyboard is what last moved it, so it takes the focus ring. */
  isKeyboardHighlighted: boolean;
  isSelected: boolean;
  item: WorkspaceAgentBrandKitMentionItem;
  onHighlight: (index: number) => void;
  onPreviewAnchorElementChange?: (element: HTMLElement | null) => void;
  /** By id, not index: a same-query refetch between paint and click can move this item. */
  onSelect: (itemId: string) => void;
};

/**
 * One row of the brand kit tab. Today the tab only lists file values, so this always draws
 * `WorkspaceAgentMentionImageRow` — a still and a label, on the same chrome the asset tab uses.
 * Colour and font values have no pill on the wire yet; when they join
 * `WorkspaceAgentBrandKitMentionItem` they get a branch of their own here (a swatch, a specimen),
 * which is why the kit tab has a row rather than borrowing the asset one.
 */
export const WorkspaceAgentBrandKitMentionRow = memo(
  ({
    id,
    index,
    isKeyboardHighlighted,
    isSelected,
    item,
    onHighlight,
    onPreviewAnchorElementChange,
    onSelect,
  }: WorkspaceAgentBrandKitMentionRowProps) => (
    <WorkspaceAgentMentionImageRow
      glyph={BRAND_KIT_FILE_GLYPH}
      id={id}
      index={index}
      isKeyboardHighlighted={isKeyboardHighlighted}
      isSelected={isSelected}
      itemId={item.id}
      onHighlight={onHighlight}
      onPreviewAnchorElementChange={onPreviewAnchorElementChange}
      onSelect={onSelect}
      thumbnailUrl={item.thumbnailUrl}
      title={item.title}
    />
  ),
);

WorkspaceAgentBrandKitMentionRow.displayName = 'WorkspaceAgentBrandKitMentionRow';
